import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Skeleton,
  Switch,
  Table,
  Tag,
  Tooltip,
  Upload,
  notification,
  theme,
} from "antd";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  SendOutlined,
  SignatureOutlined,
  UserOutlined,
  TeamOutlined,
  BorderlessTableOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import PaperCategoryAPI from "../../restAPI/PaperCategoryAPI";
import MeetingAPI from "../../restAPI/MeetingAPI";
import AnnotationAPI from "../../restAPI/AnnotationAPI";
import dayjs from "dayjs";
import AddNewDocument from "./AddNewDocument";
import MultiDocumentUploader from "./MultiDocumentUploader";

const { TextArea } = Input;
const { Dragger } = Upload;

const shareOptions = [
  {
    value: "all",
    label: (
      <span>
        <TeamOutlined style={{ marginRight: 8 }} />
        All
      </span>
    ),
  },
  {
    value: "not shared",
    label: (
      <span>
        <UserOutlined style={{ marginRight: 8 }} />
        Not Shared
      </span>
    ),
  },
  {
    value: "individual",
    label: (
      <span>
        <BorderlessTableOutlined style={{ marginRight: 8 }} />
        Individual
      </span>
    ),
  },
  {
    value: "committee",
    label: (
      <span>
        <TeamOutlined style={{ marginRight: 8 }} />
        Committee
      </span>
    ),
  },
  {
    value: "committee and additional",
    label: (
      <span>
        <TeamOutlined style={{ marginRight: 8 }} />
        Committee and Additional
      </span>
    ),
  },
];

const allowedFileTypes = ["application/pdf"];

const AddAgendaItem = forwardRef((props, ref) => {
  const { actionState, committee, agenda, agendaID, meetingCommonInfo } = useSelector(state => state.meeting);
  const [form] = Form.useForm();
  const [documentForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const { companyId, token, userCompany, role } = useSelector(state => state.auth);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [shareWithType, setShareWithType] = useState("committee");
  const [uploading, setUploading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [paperCategoryList, setPaperCategoryList] = useState([]);
  const [votingEndTypeValue, setVotingEndTypeValue] = useState("after");
  const [customDate, setCustomDate] = useState(null);
  const [currentCompanyInfo, setCurrentCompanyInfo] = useState({});

  let validationTimer = null;

  // FIX: Add theme variables
  const {
    token: {
      colorBgContainer,
      colorPrimary,
      borderRadiusLG,
      colorTextDescription,
    },
  } = theme.useToken();

  // FIX: Add upload state tracking to prevent multiple uploads
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    isCompleted: false,
    uploadId: null,
    error: null
  });

  // FIX: Add ref to track if upload is in progress
  const uploadInProgressRef = useRef(false);

  const [selectedCommitteeId, setSelectedCommitteeId] = useState(null);
  const [addedDocTableData, setAddedDocTableData] = useState([]);
  const [selectedCommittees, setSelectedCommittees] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [participantRows, setParticipantRows] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  // Memo store for original shareWith per document id (used in edit mode)
  const shareWithMemoRef = useRef({});
  const mandatoryMemberIds = useMemo(() => {
    const secId = meetingCommonInfo?.secretaryId;
    const asstId = meetingCommonInfo?.assistantSecretaryId;
    const presentIds = participantRows.map(p => p.id);
    const ids = [];
    if (secId && presentIds.includes(secId)) ids.push(secId);
    if (asstId && presentIds.includes(asstId)) ids.push(asstId);
    return Array.from(new Set(ids));
  }, [meetingCommonInfo, participantRows]);

  const [tempFileList, setTempFileList] = useState([]);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [newDocOpenAction, setNewDocOpenAction] = useState("add");
  const [selectedAttachmentDetails, setSelectedAttachmentDetails] =
    useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [attachement, setAttachement] = useState([]);

  const [open, setOpen] = useState(false);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [uploaderKey, setUploaderKey] = useState(0);
  const uploaderRef = useRef(null);
  const [hasPendingFiles, setHasPendingFiles] = useState(false);
  const [uploaderUploading, setUploaderUploading] = useState(false);
  const [hasUploaderNameErrors, setHasUploaderNameErrors] = useState(false);

  const showDrawer = () => {
    setNewDocOpenAction("add");
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    props.setAllowVoting(false);
    props.setVotingData({
      isEnabled: false,
      end: {
        type: "after",
        customDate: null,
        customTime: null,
      },
    });
    setSelectedAttachmentDetails(null);
  };
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement,
    });
  };

  useImperativeHandle(ref, () => ({
    validateAndRetrieveData: async () => {
      try {
        const values = await form.validateFields();
        return values;
      } catch (error) {
        throw error;
      }
    },
    resetFormFields: () => {
      form.resetFields();
      documentForm.resetFields();
      setFileList([]);
      props.setAttachement([]);
      setAttachement([]);
      setAddedDocTableData([]);
      props.setAllowVoting(false);
      setVotingEndTypeValue("after");
      setShareWithType("committee");
      // Reset to committee default and preselect committee members
      const committeeIds = participantRows.filter(p => p.isCommitteeMember === true).map(p => p.id);
      setSelectedMemberIds(committeeIds);
      documentForm.setFieldsValue({ shareWith: "committee", members: committeeIds });
    },
  }));

  const getPaperCategoryListByselectedCommitteeId = async () => {
    if (selectedCommitteeId !== null) {
      try {
        const response = await PaperCategoryAPI.getPaperCategoryList(
          selectedCommitteeId,
          companyId,
          token
        );
        if (response.status === 200) {
          const activePaperCategoryList = response.data.data
            ? response.data.data
              .filter((item) => item.status)
              .map((category) => {
                return {
                  label: category.name,
                  value: category.id,
                };
              })
            : [];
          setPaperCategoryList(activePaperCategoryList);
        }
      } catch (error) {
        console.log("error in addAgendaItem :", error);
      }
    }
  };

  useEffect(() => {
    const companyInfo = userCompany.companyData.find(
      (company) => company.companyId === parseInt(companyId)
    );
    setCurrentCompanyInfo(companyInfo);
  }, [companyId]);

  useEffect(() => {
    // if (props.agendaItemOpenState !== "view" && props.openAddNewAgendaDrawer) {
    if (props.openAddNewAgendaDrawer) {
      setUploaderKey(k => k + 1);
      setCustomDate(null);
      form.setFieldsValue({ paperStatus: "published" });
      documentForm.setFieldsValue({ shareWith: "committee" });
      props.setDocumentOpenState('add');
      setSelectedDocument(null);
      setMeetingParticipants(
        committee.participant?.members?.map((member) => ({
          label: member.name,
          value: member.userDetailsId,
        })) || []
      );
      // Build participant rows for unified selection table
      const rows = committee.participant.members?.map((member) => ({
        id: member.userDetailsId,
        name: member.name,
        isCommitteeMember: Boolean(member.isCommitteeMember),
      })) || [];
      setParticipantRows(rows);
      // Default to 'committee' and preselect committee members (ensure mandatory are included)
      const committeeIds = rows.filter(r => r.isCommitteeMember === true).map(r => r.id);
      const merged = Array.from(new Set([...(committeeIds || []), ...mandatoryMemberIds]));
      setSelectedMemberIds(merged);
      documentForm.setFieldsValue({ members: merged });
      setShareWithType("committee");
    }
  }, [props.agendaItemOpenState, props.openAddNewAgendaDrawer]);

  useEffect(() => {
    if (props.openAddNewAgendaDrawer && (props.agendaItemOpenState === "view" || props.agendaItemOpenState === "edit")) {
      const { name, code, comment, number, status, categoryId, categoryName, attachments, } = props.selectedPaper;
      const drawerOpenState = props.agendaItemOpenState;

      form.setFieldsValue({
        paperName: name,
        paperCategory:
          drawerOpenState === "view"
            ? {
              value: props.selectedCategory.categoryId,
              label: props.selectedCategory.categoryName,
            }
            : {
              value: props.selectedCategory.categoryId,
              label: props.selectedCategory.categoryName,
            },
        paperNo: number,
        paperCode: code,
        comment: comment,
        paperStatus: status,
      });

      const documentTableData = attachments.map((item) => ({
        _id: item._id,
        key: item.id,
        id: item.id,
        name: item.name,
        // created: dayjs(item.created).format("YYYY-MM-DD | hh:mm A"),
        created: item.created,
        file: item.file,
        comment: item.comment,
        shareWith: item.shareWith,
        order: item.order,
        voting: {
          count: item?.voting?.count,
          end: {
            customDate:
              item.isAddedToDB && item?.voting?.end?.date
                ? item.voting.end.date
                : item?.voting?.end?.customDate,
            type: item?.voting?.end?.type,
          },
          isEnabled: item?.voting?.isEnabled,
        },
        fileName: item.fileName,
        isPdf: item.isPdf,
        isAddedToDB: item.isAddedToDB,
      }));
      props.setDocumentArray(documentTableData);
    }
  }, [
    props.agendaItemOpenState,
    props.openAddNewAgendaDrawer,
  ]);

  useEffect(() => {
    setSelectedCommitteeId(committee?.participant?.committeeId);
    if (
      role.some(
        (r) =>
          r.roleName === "Secretary" || r.roleName === "Assistant Secretary"
      )
    ) {
      getPaperCategoryListByselectedCommitteeId();
    }
  }, [selectedCommitteeId]);

  useEffect(() => {
    if (shareWithType === "all") {
      const allIds = participantRows.map(p => p.id);
      const merged = Array.from(new Set([...(allIds || []), ...mandatoryMemberIds]));
      setSelectedMemberIds(merged);
      documentForm.setFieldsValue({ members: merged });
    }
  }, [shareWithType, participantRows, mandatoryMemberIds]);

  const memoizedPaperCategoryList = useMemo(() => paperCategoryList, [paperCategoryList]);
  const displayedRows = useMemo(() => {
    if (shareWithType === "committee") {
      return participantRows.filter(p => p.isCommitteeMember === true);
    }
    return participantRows;
  }, [participantRows, shareWithType]);

  const handleViewAttachment = (record) => {
    setSelectedAttachmentDetails(record);
    setNewDocOpenAction("view");
    setOpen(true);
  };

  const handleRemoveAttachment = (record) => {
    const filteredArray = props.documentArray.filter(
      (item) => item.id !== record.id
    );
    props.setDocumentArray(filteredArray);
  };

  const handleSingleAttachmentDownload = async (record) => {
    const payload = {
      itemId: props.selectedPaper._id,
      attachmentId: record._id,
      filePath: record.file,
    };

    try {
      const response = await MeetingAPI.downloadPaperItemSingleAttachment(
        agendaID,
        payload,
        companyId,
        token
      );
      if (response.status === 200) {
        const fileType = response.headers["content-type"];

        const validTypes = [
          "application/pdf", // PDF files
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/msword", // .doc
          "text/csv", // .csv
          "application/vnd.ms-powerpoint", // .ppt
          "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
        ];

        if (!validTypes.includes(fileType)) {
          openNotificationWithIcon(
            "error",
            "Abort",
            `Unsupported file type. Cannot download this file.`,
            "bottomLeft"
          );
          return;
        }

        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: fileType })
        );
        const link = document.createElement("a");
        link.href = url;

        let fileExtension;
        if (
          fileType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          fileExtension = "xlsx";
        } else if (fileType === "application/msword") {
          fileExtension = "doc";
        } else if (fileType === "text/csv") {
          fileExtension = "csv";
        } else if (fileType === "application/vnd.ms-powerpoint") {
          fileExtension = "ppt";
        } else if (
          fileType ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ) {
          fileExtension = "pptx";
        } else if (fileType.includes("/")) {
          fileExtension = fileType.split("/")[1];
        } else {
          fileExtension = "file";
        }

        const originalName = record.fileName || `downloaded_file.${fileExtension}`;
        const baseName = originalName.replace(/\.[^/.]+$/, ''); // removes extension
        const fileName = `${baseName}.${fileExtension}`;

        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log("Error in single attachment download :", error);
      openNotificationWithIcon(
        "error",
        "Abort",
        `Single attachment download failed.`,
        "bottomLeft"
      );
    }
  };

  const onFilesUpload = useCallback(async (uploadedFiles) => {
    const shareWithValue = documentForm.getFieldValue('shareWith') || 'committee';
    const membersValue = documentForm.getFieldValue('members') || [];

    props.setDocumentArray(prev => {
      const maxOrder = prev.length > 0 ? Math.max(...prev.map(d => d.order || 0)) : 0;
      const newFiles = uploadedFiles.filter(f =>
        !prev.some(existing =>
          (f.annotationUrl && existing.file === f.annotationUrl) ||
          (existing.fileName === f.name && existing.size === f.size)
        )
      );
      return [
        ...prev,
        ...newFiles.map((f, idx) => ({
          _id: null,
          id: Math.random().toString(36).substr(2, 9),
          name: f.name,
          order: maxOrder + idx + 1,
          file: f.annotationUrl || 'N/A',
          fileName: f.name,
          size: f.size,
          shareWith: { type: shareWithValue, members: membersValue },
          eSignature: { members: [] },
          created: dayjs().format('YYYY-MM-DD | hh:mm A'),
          voting: props.votingData,
          isPdf: f.name?.split('.').pop()?.toLowerCase() === 'pdf',
          isAddedToDB: false,
        })),
      ];
    });
  }, [props.votingData]); 

  const handleSinglePaperItemEdit = (record) => {
    setSelectedAttachmentDetails(record);
    setNewDocOpenAction("edit");
    setOpen(true);
  };

  useEffect(() => {
    const updatedTableData = attachement.map((item, index) => ({
      key: index,
      id: item.id,
      attachementName: item.name,
      date: item.created,
      file: item.file,
    }));
    setAddedDocTableData(updatedTableData);
  }, [attachement]);

  const checkIfAddButtonShouldBeEnabled = () => {

    if (validationTimer) clearTimeout(validationTimer);

    validationTimer = setTimeout(() => {
      const values = documentForm.getFieldsValue();
      const errors = documentForm.getFieldsError();
      const hasErrors = errors.some((field) => field.errors.length > 0);

      const documentNameFilled =
        typeof values.documentName === 'string' && values.documentName.trim().length > 0;

      const shareWithFilled = !!values.shareWith;

      const requiresMembers = ["individual", "committee", "committee and additional"].includes(values.shareWith);
      const membersFilled = requiresMembers ? Array.isArray(values.members) && values.members.length > 0 : true;

      const requiredFieldsFilled = documentNameFilled && shareWithFilled && membersFilled;

      const isCustomDateExist = props.votingData.end.type == "custom";
      let isCustomDateInvalid = false;
      if (isCustomDateExist) {
        const customDate = props.votingData.end.customDate;
        isCustomDateInvalid = dayjs(customDate || 1970).isBefore(dayjs(), 'day');
      }

      props.setIsAddDisable(!(requiredFieldsFilled && (props.uploadFileDetails?.savedFileName || selectedDocument?.fileName) && !hasErrors && !isCustomDateInvalid));
    }, 50);
  };


  useEffect(() => {
    checkIfAddButtonShouldBeEnabled();
  }, [props.allowVoting, props.votingData]);


  const handleShareWithChange = (value) => {
    setShareWithType(value);
    form.resetFields(["members"]);

    // Clear selected individuals when shareWith is changed
    documentForm.setFieldsValue({
      members: [],
    });
    // If editing a document and reverting to its original share type, restore memo'd members
    if (props.documentOpenState === 'edit' && selectedDocument && shareWithMemoRef.current[selectedDocument.id]) {
      const memo = shareWithMemoRef.current[selectedDocument.id];
      if (value === memo.type) {
        const restored = Array.from(new Set([...(Array.isArray(memo.members) ? memo.members : []), ...mandatoryMemberIds]));
        setSelectedMemberIds(restored);
        documentForm.setFieldsValue({ members: restored });
        return;
      }
    }
    // Adjust selection based on type
    if (value === "all") {
      const allIds = participantRows.map(p => p.id);
      const merged = Array.from(new Set([...(allIds || []), ...mandatoryMemberIds]));
      setSelectedMemberIds(merged);
      documentForm.setFieldsValue({ members: merged });
    } else if (value === "committee") {
      const committeeIds = participantRows.filter(p => p.isCommitteeMember === true).map(p => p.id);
      const merged = Array.from(new Set([...(committeeIds || []), ...mandatoryMemberIds]));
      setSelectedMemberIds(merged);
      documentForm.setFieldsValue({ members: merged });
    } else if (value === "committee and additional") {
      // Preselect only committee members; additional remain unselected
      const committeeIds = participantRows.filter(p => p.isCommitteeMember === true).map(p => p.id);
      const merged = Array.from(new Set([...(committeeIds || []), ...mandatoryMemberIds]));
      setSelectedMemberIds(merged);
      documentForm.setFieldsValue({ members: merged });
    } else if (value === "individual") {
      // Start with mandatory members only
      setSelectedMemberIds(mandatoryMemberIds);
      documentForm.setFieldsValue({ members: mandatoryMemberIds });
    } else if (value === "not shared") {
      // Keep mandatory members selected even if not displayed
      setSelectedMemberIds(mandatoryMemberIds);
      documentForm.setFieldsValue({ members: mandatoryMemberIds });
    }
  };

  const handleVotingSwitch = (checked) => {
    props.setAllowVoting(checked);
    if (checked) {
      props.setVotingData(prev => ({
        ...prev,
        isEnabled: true,
        end: {
          ...prev.end,
          type: prev.end?.type || 'after', // Set default to 'after' if null/undefined
          customDate: prev.end?.type === 'custom' ? prev.end?.customDate : null,
        },
      }));
    } else {
      setVotingEndTypeValue('after');
      setCustomDate(null);
      props.setVotingData({
        isEnabled: false,
        end: {
          type: 'after',
          customDate: null,
        },
      });
    }
  };

  // const handleVotingSwitch = (checked) => {
  //   props.setAllowVoting(checked);
  //   if(!checked){
  //     setVotingEndTypeValue('after')
  //   }
  // };

  const onChange = ({ target: { value } }) => {
    setVotingEndTypeValue(value);
    props.setVotingData({
      isEnabled: props.allowVoting,
      end: {
        type: value,
        customDate:
          value === "custom" ? customDate?.format("YYYY-MM-DD HH:mm") : null,
      },
    });
  };

  useEffect(() => {
    checkIfAddButtonShouldBeEnabled();
  }, [props.uploadFileDetails]);

  const handleValidateDate = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Date is required."));
    }
    if (value && value.isBefore(dayjs(), "day")) {
      return Promise.reject(new Error("Date cannot be earlier than today."));
    }
    return Promise.resolve();
  };

  // Disable past dates
  const disabledDate = (current) => {
    if (!current) {
      return false;
    }
    // Disable dates before today
    return current.isBefore(dayjs(), "day");
  };

  // Disable past times when today is selected
  const disabledTime = (current) => {
    if (!current) {
      return {};
    }

    const now = dayjs();
    const minTime = now.add(5, "minute");

    // If selected date is today, restrict times
    if (current.isSame(now, "day")) {
      const disabledHours = [];
      const disabledMinutes = {};

      // Disable hours before current hour
      for (let hour = 0; hour < now.hour(); hour++) {
        disabledHours.push(hour);
      }

      // For the current hour, disable minutes before (current minute + 5)
      if (current.hour() === now.hour()) {
        const disabledMins = [];
        for (let minute = 0; minute <= minTime.minute(); minute++) {
          disabledMins.push(minute);
        }
        disabledMinutes[now.hour()] = disabledMins;
      }

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: (selectedHour) => {
          // If selected hour is current hour, return disabled minutes
          if (selectedHour === now.hour()) {
            return disabledMinutes[selectedHour] || [];
          }
          // For future hours today, allow all minutes
          return [];
        },
      };
    }

    // For future dates, allow all times
    return {};
  };

  const onDateChange = (date) => {
    setCustomDate(date);
    props.setVotingData({
      isEnabled: true,
      end: {
        type: votingEndTypeValue,
        customDate:
          votingEndTypeValue === "custom" ? dayjs(date).toISOString() : null,
      },
    });
  };

  const resetDocumentForm = () => {
    documentForm.resetFields();
    setFileList([]);
    props.setAllowVoting(false);
    setVotingEndTypeValue("after");
    setShareWithType("committee");
    setCustomDate(null);
    // Reset to committee default and preselect committee members (include mandatory)
    const committeeIds = participantRows.filter(p => p.isCommitteeMember === true).map(p => p.id);
    const merged = Array.from(new Set([...(committeeIds || []), ...mandatoryMemberIds]));
    setSelectedMemberIds(merged);
    documentForm.setFieldsValue({ shareWith: "committee", members: merged });
    props.setDocumentOpenState('add');
    props.setUploadFileDetails(null);
    checkIfAddButtonShouldBeEnabled();
    // FIX: Reset upload state when form is reset
    setUploadState({
      isUploading: false,
      isCompleted: false,
      uploadId: null,
      error: null
    });
    uploadInProgressRef.current = false;
  };

  const handleDocumentAdd = async () => {
    try {
      await documentForm.validateFields();

      if (fileList.length === 0 && props.agendaItemOpenState === "add") {
        openNotificationWithIcon("error", "Document adding failed", "Please upload a file before submitting.", "bottomLeft");
        // throw new Error("Please upload a file before submitting.");
        return;
        // } else if (props.agendaItemOpenState === "add" && props.documentOpenState === 'add') {
      } else if (props.documentOpenState === 'add') {
        const { documentName, shareWith, members = [] } = documentForm.getFieldsValue();
        const randomId = Math.random().toString(36).substr(2, 9);
        props.setDocumentArray((prev) => {
          const maxOrder =
            prev.length > 0
              ? Math.max(...prev.map((item) => item.order || 0))
              : 0;
          let newDocument = [
            ...prev,
            {
              _id: null,
              id: randomId,
              name: documentName,
              order: maxOrder + 1,
              file: props.uploadFileDetails?.savedFileName ? props.uploadFileDetails?.savedFileName : 'N/A',
              fileName: props.uploadFileDetails?.originalFileName,
              shareWith: {
                type: shareWith ? shareWith : 'all',
                members: members.length > 0 ? members : []
              },
              eSignature: {
                members: []
              },
              // created: dayjs().format('YYYY-MM-DD'),
              created: dayjs().format("YYYY-MM-DD | hh:mm A"),
              voting: props.votingData,
              isPdf: props.uploadFileDetails?.savedFileName ? props.uploadFileDetails?.savedFileName.split('.').pop().toLowerCase() === 'pdf' : false,
              isAddedToDB: false
            },
          ];
          console.log("New Document to be added :", newDocument);
          return newDocument;
        });
        openNotificationWithIcon('success', 'Document added', 'Succefully added a new document.', 'bottomLeft');
        resetDocumentForm();
      } else if (props.documentOpenState === 'edit') {
        const value = documentForm.getFieldsValue();

        const documentIndex = props.documentArray.findIndex(item => item.id === selectedDocument.id);
        const { documentName, shareWith, members = [] } = documentForm.getFieldsValue();
        const { id, file, fileName, order = 1 } = selectedDocument;

        const updatedDocument = {
          _id: id,
          id: id,
          name: documentName,
          file: props.uploadFileDetails?.savedFileName ? props.uploadFileDetails?.savedFileName : file,
          fileName: props.uploadFileDetails?.originalFileName ? props.uploadFileDetails?.originalFileName : fileName,
          shareWith: {
            type: shareWith ? shareWith : 'all',
            members: members?.length > 0 ? members : [],
          },
          eSignature: {
            members: [],
          },
          created: dayjs().format('YYYY-MM-DD | hh:mm A'),
          voting: props.votingData,
          order: order ? order : 1,
          isPdf: true,
          isAddedToDB: false
        };
        if (documentIndex !== -1) {
          const updatedAttachments = props.documentArray.map((item, index) =>
            index === documentIndex ? updatedDocument : item
          );
          openNotificationWithIcon("success", "Successful", `Attachment details updated successfully.`, "bottomLeft");
          props.setDocumentArray(updatedAttachments);
          resetDocumentForm();
        } else {
          openNotificationWithIcon("error", "Update Failed", `Attachment details update failed.`, "bottomLeft");
        }
      }
    } catch (error) {
      console.log("Error in handleDocumentAdd :", error);
    } finally {
      props.setIsAddDisable(true);
      props.setUploadFileDetails(null);
    }
  };

  const handleDocumentClick = (openState, documentItem) => {
    const { file, fileName, name, shareWith, voting } = documentItem;
    const customeDateValue = voting?.end?.customDate ? dayjs(voting?.end?.customDate) : null;
    setCustomDate(customeDateValue);
    setFileList([
      {
        uid: `${Date.now()}`,
        name: fileName,
        status: "done",
        url: file || "",
      },
    ]);
    const preselected = Array.isArray(shareWith?.members) ? shareWith.members : [];
    const mergedMembers = Array.from(new Set([...(preselected || []), ...mandatoryMemberIds]));
    documentForm.setFieldsValue({
      documentName: name,
      shareWith: shareWith?.type || "all",
      members: mergedMembers,
    });
    props.setUploadFileDetails({
      originalFileName: fileName,
      savedFileName: file
    });
    setShareWithType(shareWith.type);
    setSelectedMemberIds(mergedMembers);
    setVotingEndTypeValue(voting?.end?.type || 'after');
    // setCustomDate(dayjs(voting?.end?.customDate));
    props.setAllowVoting(voting.isEnabled);
    props.setVotingData({
      // count: voting?.count,
      end: {
        customDate: voting?.end?.customDate,
        type: voting?.end?.type,
      },
      isEnabled: voting?.isEnabled,
    });
    documentForm.setFieldsValue({ customDate: customeDateValue });
    // Memo original shareWith for this document id
    shareWithMemoRef.current[documentItem.id] = {
      type: shareWith?.type || 'all',
      members: Array.isArray(shareWith?.members) ? shareWith.members : []
    };
  };

  const handleRemoveDocument = (recordId) => {
    const filteredDocuments = props.documentArray.filter(item => item.id !== recordId);
    props.setDocumentArray(filteredDocuments);
    resetDocumentForm();
  };

  const handleDocVotingToggle = (docId, checked) => {
    props.setDocumentArray(prev =>
      prev.map(doc =>
        doc.id === docId ? { ...doc, voting: { ...doc.voting, isEnabled: checked } } : doc
      )
    );
  };

  const getMemberDisplayNames = (shareWith) => {
    if (!shareWith || shareWith.type === 'all') return 'All participants';
    if (shareWith.type === 'not shared') return 'Not shared';
    const ids = shareWith.members || [];
    if (!ids.length) return '—';
    const names = ids.map(id => {
      const m = participantRows.find(p => p.id === id);
      if (!m) return null;
      const parts = m.name.trim().split(' ');
      return parts.length > 1 ? `${parts[0]} ${parts[1].charAt(0)}.` : parts[0];
    }).filter(Boolean);
    if (!names.length) return '—';
    const shown = names.slice(0, 2).join(', ');
    return names.length > 2 ? `${shown} +${names.length - 2}` : shown;
  };

  const shareTagConfig = {
    all: { color: 'blue', label: 'All' },
    committee: { color: 'purple', label: 'Committee' },
    individual: { color: 'green', label: 'Individual' },
    "committee and additional": {
      color: 'orange',
      label: 'Committee and Additional',
    },
    'not shared': { color: 'default', label: 'Not Shared' },
  };

  const colTitle = (text) => (
    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, color: colorTextDescription }}>
      {text}
    </span>
  );

  const docTableColumns = [
    {
      title: colTitle('#'),
      key: 'order',
      width: 36,
      render: (_, __, idx) => (
        <span style={{ color: colorTextDescription }}>{idx + 1}</span>
      ),
    },
    {
      title: colTitle('DOCUMENT NAME'),
      key: 'name',
      render: (_, item) => {                
        const sizeText = item.size
          ? (item.size < 1024 * 1024
            ? `${(item.size / 1024).toFixed(1)} KB`
            : `${(item.size / (1024 * 1024)).toFixed(1)} MB`)
          : null;
        return (
          <div>
            <div style={{ fontWeight: 600, wordBreak:'break-word', overflowWrap:'break-word' }}>{item.name || item.fileName}</div>
            <div style={{ fontSize: 12, color: colorTextDescription }}>
              {sizeText ? `${sizeText}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: colTitle('SHARE WITH'),
      key: 'shareWith',
      width: 130,
      render: (_, item) => {        
        const cfg = shareTagConfig[item.shareWith?.type] || shareTagConfig['not shared'];
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: colTitle('VOTING'),
      key: 'voting',
      width: 80,
      render: (_, item) => (
        <Switch
          size="small"
          checked={item.voting?.isEnabled}
          onChange={(checked) => handleDocVotingToggle(item.id, checked)}
          disabled={props.agendaItemOpenState === 'view' || actionState === 'view'}
        />
      ),
    },
    {
      title: colTitle('ACTIONS'),
      key: 'actions',
      width: 110,
      render: (_, item) => (
        <Flex gap={4}>
          <Tooltip title="View">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                props.setDocumentOpenState('view');
                handleDocumentClick('view', item);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedDocument(item);
                props.setDocumentOpenState('edit');
                handleDocumentClick('edit', item);
              }}
              disabled={props.agendaItemOpenState === 'view' || actionState === 'view'}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveDocument(item?.id)}
              disabled={props.agendaItemOpenState === 'view' || actionState === 'view'}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Skeleton active loading={skeletonLoading}>
        <Form layout="vertical" form={form}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Paper Category"
                name="paperCategory"
                rules={[
                  { required: true, message: "Please select paper category" },
                ]}
              >
                <Select
                  options={memoizedPaperCategoryList}
                  placeholder="Select a paper category"
                  disabled={
                    props.agendaItemOpenState === "view" ||
                    actionState === "view"
                  }
                  labelInValue
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Paper Name"
                name="paperName"
                rules={[
                  {
                    required: true,
                    message: "Please enter the paper name",
                  },
                  {
                    max: 150,
                    message: "Paper name cannot exceed 150 characters", // requested by mihiru UAT 2026.06.10
                  },
                ]}
              >
                <Input
                  disabled={
                    props.agendaItemOpenState === "view" ||
                    actionState === "view"
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Paper Code"
                name="paperCode"
                rules={[
                  // { required: true, message: "Please select paper code" },
                  {
                    max: 20,
                    message: "Paper code should be below 20 characters.",
                  },
                ]}
              >
                <Input
                  disabled={
                    props.agendaItemOpenState === "view" ||
                    actionState === "view"
                  }
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Status"
                name="paperStatus"
                initialValues={{
                  paperStatus: "published",
                }}
                rules={[
                  {
                    required: true,
                    message: "Please select the paper status",
                  },
                ]}
              >
                <Select
                  options={[
                    {
                      label: "Unpublish",
                      value: "not shared",
                    },
                    {
                      label: "Publish",
                      value: "published",
                    },
                  ]}
                  disabled={
                    props.agendaItemOpenState === "view" ||
                    actionState === "view"
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Comment"
                name="comment"
                rules={[
                  {
                    max: 250,
                    message: "Comment should be below 250 characters.",
                  },
                ]}
              >
                <TextArea
                  disabled={
                    props.agendaItemOpenState === "view" ||
                    actionState === "view"
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          {(props.agendaItemOpenState !== "view" || (props.agendaItemOpenState === "view" && props.documentOpenState === 'view')) && (<>
            <Col span={24}>
              <Divider>Add Documents</Divider>
            </Col>
            <Form layout="vertical" form={documentForm}
              onValuesChange={checkIfAddButtonShouldBeEnabled}
            >

              {props.documentOpenState !== 'edit' && (
                <Col span={24}>
                  <MultiDocumentUploader
                    key={uploaderKey}
                    ref={uploaderRef}
                    disabled={
                      actionState === "view" ||
                      props.documentOpenState === "view" ||
                      props.agendaItemOpenState === "view"
                    }
                    token={token}
                    companyId={companyId}
                    meetingCommonInfo={meetingCommonInfo}
                    currentCompanyInfo={currentCompanyInfo}
                    onQueueChange={(count) => setUploadedFileCount(count)}
                    onHasPendingChange={setHasPendingFiles}
                    onUploadingChange={setUploaderUploading}
                    onHasNameErrorsChange={setHasUploaderNameErrors}
                    beforeUpload={documentForm.validateFields}
                    onFilesUploaded={onFilesUpload}
                  />
                </Col>
              )}
              <Row gutter={24}>
                {(props.documentOpenState === 'edit' || props.documentOpenState === 'view') && (
                  <Col span={12} style={{ marginBlockStart: 10 }}>
                    <Form.Item
                      label="Document Name"
                      name="documentName"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the document name",
                        },
                        {
                          max: 150,
                          message: "Document name cannot exceed 150 characters",
                        },
                      ]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <Input
                        disabled={
                          actionState === "view" ||
                          props.documentOpenState === "view" ||
                          props.agendaItemOpenState === "view"
                        }
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col span={(props.documentOpenState === 'edit' || props.documentOpenState === 'view') ? 12 : 24} style={{ marginBlockStart: 10 }}>
                  <Form.Item
                    label="Share With"
                    name="shareWith"
                    rules={[
                      {
                        required: true,
                        message: "Please select one option",
                      },
                    ]}
                    initialValue={'committee'}
                  >
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Category to share with"
                      options={shareOptions}
                      onChange={handleShareWithChange}
                      disabled={
                        actionState === "view" ||
                        props.documentOpenState === "view" ||
                        props.agendaItemOpenState === "view"
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              {/* Unified participant selection - hidden for 'not shared' */}
              {shareWithType !== 'not shared' && (
                <Col span={24}>
                  <Form.Item
                    label="Select Participants"
                    name="members"
                    rules={[
                      ...(shareWithType === "individual" || shareWithType === "committee" || shareWithType === "committee and additional"
                        ? [{ required: true, message: "Please select at least one participant" }]
                        : []),
                    ]}
                  >
                    <Table
                      rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: selectedMemberIds,
                        onChange: (selectedRowKeys) => {
                          // Always ensure mandatory members remain selected
                          const merged = Array.from(new Set([...(selectedRowKeys || []), ...mandatoryMemberIds]));
                          setSelectedMemberIds(merged);
                          documentForm.setFieldsValue({ members: merged });
                          checkIfAddButtonShouldBeEnabled();
                        },
                        columnTitle: null,
                        hideSelectAll: true,
                        getCheckboxProps: (record) => ({
                          disabled:
                            actionState === "view" ||
                            props.documentOpenState === "view" ||
                            props.agendaItemOpenState === "view" ||
                            shareWithType === "all" ||
                            mandatoryMemberIds.includes(record.id),
                        })
                      }}
                      columns={[
                        (shareWithType === "committee and additional" || shareWithType === "all" || shareWithType === "individual")
                          ? {
                            // title: 'Participant',
                            key: 'participant',
                            render: (_, record) => (
                              <Flex align="center" justify="space-between" style={{ width: '100%' }}>
                                <span>{record.name}</span>
                                <Tag color={record.isCommitteeMember === true ? 'blue' : 'purple'}>
                                  {record.isCommitteeMember === true ? 'Committee Member' : 'Additional Member'}
                                </Tag>
                              </Flex>
                            ),
                          }
                          : {
                            // title: 'Name',
                            dataIndex: 'name',
                            key: 'name'
                          },
                      ]}
                      dataSource={displayedRows}
                      rowKey="id"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        itemRender: (page, type, originalElement) => {
                          if (type === 'page') {
                            return (
                              <span style={{ margin: '0 6px', display: 'inline-block' }}>
                                {originalElement}
                              </span>
                            );
                          }
                          return originalElement;
                        },
                      }}
                      size="small"
                    />
                  </Form.Item>
                </Col>
              )}
              {(props.documentOpenState === 'edit' || props.documentOpenState === 'view' || uploadedFileCount >= 1) && (
                <>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label="You can allow voting for this document"
                        name="allowVoting"
                      >
                        Allow Voting{" "}
                        <Switch
                          size="small"
                          style={{ marginLeft: '15px' }}
                          checked={props.allowVoting}
                          onChange={handleVotingSwitch}
                          disabled={
                            actionState === "view" ||
                            props.documentOpenState === "view" ||
                            props.agendaItemOpenState === "view"
                          }
                        />
                      </Form.Item>
                    </Col>
                    {props.allowVoting && (
                      <Col span={12}>
                        <Form.Item label="Voting Ends">
                          <Radio.Group
                            optionType="button"
                            options={[
                              {
                                label: "With the meeting",
                                value: "after",
                              },
                              {
                                label: "Custom timing",
                                value: "custom",
                              },
                            ]}
                            onChange={onChange}
                            value={votingEndTypeValue}
                            disabled={
                              actionState === "view" ||
                              props.documentOpenState === "view" ||
                              props.agendaItemOpenState === "view"
                            }
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  {votingEndTypeValue === "custom" && props.allowVoting && (
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          label="Select Date and time"
                          name="customDate"
                          rules={[{ validator: handleValidateDate }]}
                        >
                          <DatePicker
                            showTime={{
                              format: "HH:mm",
                            }}
                            style={{ width: "100%" }}
                            onChange={onDateChange}
                            disabledDate={disabledDate}
                            disabledTime={disabledTime}
                            disabled={
                              actionState === "view" ||
                              props.documentOpenState === "view" ||
                              props.agendaItemOpenState === "view"
                            }
                            allowClear={false}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {hasPendingFiles && props.documentOpenState !== 'edit' && props.agendaItemOpenState !== 'view' && actionState !== 'view' && (
                <Flex justify="flex-end" style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    loading={uploaderUploading}
                    disabled={uploaderUploading || hasUploaderNameErrors}
                    onClick={() => uploaderRef.current?.startUpload()}
                  >
                    Upload
                  </Button>
                </Flex>
              )}

              {props.documentOpenState === 'edit' && props.agendaItemOpenState !== "view" && (
                <Col span={24} style={{ marginBottom: 24 }}>
                  <Flex align="center" justify="flex-end" gap={4}>
                    <Button onClick={resetDocumentForm}>
                      Reset
                    </Button>
                    <Button type="primary" onClick={handleDocumentAdd} disabled={props.isAddDisable}>
                      Save
                    </Button>
                  </Flex>
                </Col>
              )}
            </Form>
          </>
          )}
          <Col span={24}>
            <Divider>Added Documents</Divider>
          </Col>
          <Col span={24}>
            <Table
              size="small"
              dataSource={
                props.documentArray?.length > 0
                  ? props.documentArray
                  : []
              }
              columns={docTableColumns}
              rowKey={(record, index) => record.id || record.key || String(index)}
              pagination={false}
              locale={{ emptyText: 'No documents added yet' }}
            />
          </Col>
        </Form>
        {contextHolder}

        <AddNewDocument
          addNewDocDrawerClose={onClose}
          addNewDocDrawerOpen={open}
          setDocumentArray={props.setDocumentArray}
          documentArray={props.documentArray}
          newDocOpenAction={newDocOpenAction}
          votingData={props.votingData}
          setVotingData={props.setVotingData}
          allowVoting={props.allowVoting}
          setAllowVoting={props.setAllowVoting}
          selectedAttachmentDetails={selectedAttachmentDetails}
          selectedCategory={props.selectedCategory}
          selectedPaper={props.selectedPaper}
          agendaItemOpenState={props.agendaItemOpenState}
          addNewDocumentDrawerOpenState={open}
        // setOpen={setOpen}
        />
      </Skeleton>
    </>
  );
});

export default AddAgendaItem;
